import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, shuffle, splitFace, useGame, useRoundTimer, type GameProps } from '../kit/shell';
import { PAIRS_FACE_DOWN, PAIRS_FACE_UP } from '../phrases';
import { KINDS } from '../../concepts/kinds';
import Pic from '../../media/Pic';

type Item = { say: string; answer: string };
type Card = { face: string; pair: number };

export default function MemoryPairs(props: GameProps) {
  const game = useGame(props);
  const items = ((game.config?.items ?? []) as Item[]).filter((i) => i?.say && i.answer);
  const perBoard = game.num('pairsPerBoard', 4);
  const faceUp = game.bool('faceUp', false);
  const speakCards = game.bool('chipSpeech', true);
  const [cards, setCards] = useState<Card[]>([]);
  const [open, setOpen] = useState<number[]>([]);
  const [matched, setMatched] = useState<number[]>([]);
  const [trial, setTrial] = useState<string | null>(null);
  const [turns, setTurns] = useState(0);
  const [done, setDone] = useState(false);
  const lock = useRef(false);

  const start = useCallback(async () => {
    if (items.length === 0) return;
    const chosen = shuffle(items).slice(0, Math.min(perBoard, items.length));
    const deck = shuffle(chosen.flatMap((p, i) => [{ face: p.say, pair: i }, { face: p.answer, pair: i }]));
    setDone(false);
    setOpen([]);
    setMatched([]);
    setTurns(0);
    const id = await game.register({ cards: deck.map((c) => c.face) }, { value: 'complete' });
    if (!id) return;
    setCards(deck);
    setTrial(id);
  }, [items.map((i) => i.say + i.answer).join('|'), perBoard]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => { if (trial) game.say(faceUp ? PAIRS_FACE_UP : PAIRS_FACE_DOWN); }, [trial]);

  async function flip(i: number) {
    if (lock.current || done || open.includes(i) || matched.includes(i)) return;
    if (speakCards) game.say(splitFace(cards[i].face).word || cards[i].face);
    const next = [...open, i];
    setOpen(next);
    if (next.length < 2) return;
    setTurns((t) => t + 1);
    const [a, b] = next;
    if (cards[a].pair === cards[b].pair) {
      const all = [...matched, a, b];
      setMatched(all);
      setOpen([]);
      if (all.length === cards.length && trial) {
        const v = await game.respond(trial, 'complete');
        if (v) setDone(true);
      }
      return;
    }
    lock.current = true;
    window.setTimeout(() => { setOpen([]); lock.current = false; }, faceUp ? 500 : 1100);
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); }, 1300);
  const cols = cards.length <= 4 ? 'cols-2' : cards.length <= 9 ? 'cols-3' : 'cols-4';

  return (
    <GameFrame game={game} empty={items.length === 0} emptyText={`${KINDS.memoryPairs.describes} Edit it to add pairs.`} onResume={() => { void start(); }} breakTrial={trial}>
      {cards.length > 0 && (
        <div className={`g-board${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text={done ? `All found in ${turns} goes` : 'Find the pairs'} />
          <div className={`tile-grid ${cols}`} role="group" aria-label="Cards">
            {cards.map((c, i) => {
              const shown = faceUp || open.includes(i) || matched.includes(i);
              const f = splitFace(c.face);
              return (
                <button
                  key={i}
                  className={`tile g-card${shown ? ' is-shown' : ''}${matched.includes(i) ? ' correct' : ''}${open.includes(i) && faceUp ? ' is-picked' : ''}`}
                  onClick={() => void flip(i)}
                  aria-label={shown ? f.word || c.face : `Card ${i + 1}, face down`}
                  disabled={matched.includes(i)}
                >
                  {shown ? (
                    <>
                      <Pic text={c.face} fallback={f.picture ? <span className="g-tile-pic" aria-hidden="true">{f.picture}</span> : null} className="g-tile-img" />
                      {f.word && <span className="g-tile-word">{f.word}</span>}
                    </>
                  ) : <span className="g-card-back" aria-hidden="true">?</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </GameFrame>
  );
}
